import React, { useState } from "react";
import Header from "../components/Header";
import { useBill } from "../hooks/useBill";
import { calculateTotalPrice } from "../utils/CommonMethod";

const BillGenerate = () => {
  const [filters, setFilters] = useState({
    orderId: "",
    userId: "",
    date: "",
  });

  const { loading, bills, generateBillByOrderId, generateBillByDate, downloadBillPDF } = useBill();
  const [fetchedBills, setFetchedBills] = useState([]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleFetchBills = async () => {
    if (!filters.orderId && !filters.userId && !filters.date) {
      alert("Please provide at least one filter (Order ID, User ID, or Date)");
      return;
    }

    try {
      let result;
      if (filters.orderId) {
        result = await generateBillByOrderId(filters.orderId);
        setFetchedBills(result ? [result] : []);
      } else if (filters.date) {
        result = await generateBillByDate(filters.date);
        setFetchedBills(result || []);
      }
    } catch (error) {
      console.error("Failed to fetch bills:", error);
      setFetchedBills([]);
    }
  };

  const handleDownloadBill = async (orderId) => {
    await downloadBillPDF(orderId);
  };

  const renderBillTable = (bill, index) => {
    const subtotal = bill.items?.reduce(
      (sum, item) => sum + calculateTotalPrice(item.price, item.quantity),
      0
    ) || 0;
    const tax = (subtotal * (bill.taxRate || 0)) / 100;
    const discount = bill.discount || 0;
    const total = subtotal + tax - discount;

    return (
      <div
        key={index}
        className="bg-white rounded-lg shadow-md border border-blue-200 p-6 mb-6"
      >
        {/* Bill Header */}
        <div className="flex justify-between items-start mb-6 pb-4 border-b border-gray-200">
          <div>
            <h3 className="text-xl font-bold text-blue-800 mb-2">
              Invoice #{bill.orderId}
            </h3>
            <div className="text-sm text-gray-600 space-y-1">
              <p>
                <span className="font-medium">Date:</span> {bill.date || "N/A"}
              </p>
              <p>
                <span className="font-medium">Customer:</span>{" "}
                {bill.customerName || "N/A"}
              </p>
              <p>
                <span className="font-medium">Mobile:</span>{" "}
                {bill.customerMobile || "N/A"}
              </p>
              <p>
                <span className="font-medium">User ID:</span>{" "}
                {bill.userId || "N/A"}
              </p>
            </div>
          </div>
          <button
            onClick={() => handleDownloadBill(bill.orderId)}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-lg hover:from-indigo-600 hover:to-indigo-500 transition shadow-md"
          >
            <i className="fas fa-download"></i>
            Download PDF
          </button>
        </div>

        {/* Cart Summary Table */}
        <div className="overflow-x-auto mb-6">
          <table className="min-w-full text-sm text-gray-800 border border-blue-200 rounded-lg overflow-hidden">
            <thead className="bg-gradient-to-r from-blue-200 via-indigo-100 to-teal-100 text-blue-900 uppercase text-xs tracking-wide">
              <tr>
                <th className="py-3 px-4 text-left">S.No</th>
                <th className="py-3 px-4 text-left">Item Name</th>
                <th className="py-3 px-4 text-center">Quantity</th>
                <th className="py-3 px-4 text-right">Unit Price</th>
                <th className="py-3 px-4 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {bill.items?.length === 0 ? (
                <tr>
                  <td
                    colSpan="5"
                    className="text-center py-8 text-gray-500 italic"
                  >
                    No items in this bill
                  </td>
                </tr>
              ) : (
                bill.items?.map((item, idx) => (
                  <tr
                    key={idx}
                    className={`border-t ${
                      idx % 2 === 0 ? "bg-white" : "bg-blue-50"
                    } hover:bg-indigo-50 transition-colors`}
                  >
                    <td className="py-3 px-4">{idx + 1}</td>
                    <td className="py-3 px-4 font-medium">{item.name}</td>
                    <td className="py-3 px-4 text-center">{item.quantity}</td>
                    <td className="py-3 px-4 text-right">
                      ₹{item.price?.toFixed(2)}
                    </td>
                    <td className="py-3 px-4 text-right font-semibold text-green-600">
                      ₹{calculateTotalPrice(item.price, item.quantity).toFixed(2)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Bill Summary */}
        <div className="flex justify-end">
          <div className="w-80 space-y-2">
            <div className="flex justify-between py-2 border-b border-gray-200">
              <span className="text-gray-700">Subtotal:</span>
              <span className="font-semibold">₹{subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-200">
              <span className="text-gray-700">
                Tax ({bill.taxRate || 0}%):
              </span>
              <span className="font-semibold">₹{tax.toFixed(2)}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-200">
              <span className="text-gray-700">Discount:</span>
              <span className="font-semibold text-green-600">
                -₹{discount.toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between py-3 bg-gradient-to-r from-blue-100 to-indigo-100 px-4 rounded-lg">
              <span className="font-bold text-lg text-blue-800">Total:</span>
              <span className="font-bold text-lg text-blue-800">
                ₹{total.toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col overflow-hidden bg-gradient-to-tr from-blue-50 via-indigo-50 to-teal-50 p-6">
      <Header showSearch={false} showCart={false} />

      <div className="flex-1 overflow-y-auto mt-6 space-y-6">
        {/* Page Header */}
        <div className="bg-white rounded-xl shadow-md border border-blue-200 p-6">
          <div className="flex items-center gap-3">
            <i className="fas fa-file-invoice text-3xl text-blue-600"></i>
            <div>
              <h1 className="text-2xl font-bold text-blue-800">Generate Bill</h1>
              <p className="text-sm text-gray-600">
                Search and generate bills using Order ID, User ID, or Date
              </p>
            </div>
          </div>
        </div>

        {/* Filter Section */}
        <section className="bg-white rounded-xl shadow-lg p-6 border border-blue-200 hover:border-indigo-300 transition">
          <div className="flex items-center gap-2 text-blue-700 mb-4">
            <i className="fas fa-filter text-xl"></i>
            <h2 className="text-xl font-semibold">Search Filters</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            <div className="flex items-center">
              <label className="w-32 text-sm font-medium text-gray-800">
                Order ID:
              </label>
              <input
                type="text"
                name="orderId"
                value={filters.orderId}
                onChange={handleFilterChange}
                placeholder="Enter Order ID"
                className="px-2 py-1 w-40 rounded border border-blue-300 focus:ring focus:ring-blue-300 focus:outline-none text-sm"
              />
            </div>

            <div className="flex items-center">
              <label className="w-32 text-sm font-medium text-gray-800">
                User ID:
              </label>
              <input
                type="text"
                name="userId"
                value={filters.userId}
                onChange={handleFilterChange}
                placeholder="Enter User ID"
                className="px-2 py-1 w-40 rounded border border-blue-300 focus:ring focus:ring-blue-300 focus:outline-none text-sm"
              />
            </div>

            <div className="flex items-center">
              <label className="w-32 text-sm font-medium text-gray-800">
                Date:
              </label>
              <input
                type="date"
                name="date"
                value={filters.date}
                onChange={handleFilterChange}
                className="px-2 py-1 w-40 rounded border border-blue-300 focus:ring focus:ring-blue-300 focus:outline-none text-sm"
              />
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            <button
              onClick={handleFetchBills}
              disabled={loading}
              className={`flex items-center gap-2 bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-indigo-600 hover:to-indigo-500 text-white font-semibold px-6 py-2 rounded-full shadow-md transition duration-200 ${
                loading ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Fetching...
                </>
              ) : (
                <>
                  <i className="fas fa-search"></i>
                  Fetch Bills
                </>
              )}
            </button>
          </div>
        </section>

        {/* Bills Display */}
        {loading ? (
          <div className="text-center py-12">
            <i className="fas fa-spinner fa-spin text-blue-500 text-4xl"></i>
            <p className="mt-4 text-gray-600">Loading bills...</p>
          </div>
        ) : fetchedBills.length > 0 ? (
          <div>
            <h2 className="text-xl font-bold text-blue-800 mb-4">
              {fetchedBills.length} Bill{fetchedBills.length > 1 ? "s" : ""} Found
            </h2>
            {fetchedBills.map((bill, index) => renderBillTable(bill, index))}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-md border border-blue-200 p-12 text-center">
            <i className="fas fa-inbox text-gray-400 text-5xl mb-4"></i>
            <p className="text-gray-600">
              No bills found. Use filters above to search.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BillGenerate;
